// Helper function to extract string value from ErrorDetail or string
export function extractValue(value) {
    if (!value) return null;
    // Handle ErrorDetail objects (Django REST framework format)
    if (typeof value === 'object' && value.string !== undefined) {
        return value.string;
    }
    // Handle plain strings
    if (typeof value === 'string') {
        return value;
    }
    return String(value);
}

// Format time for display (HH:MM from HH:MM:SS)
export function formatTimeForError(timeString) {
    if (!timeString) return '';
    const time = extractValue(timeString);
    if (!time) return '';
    // Handle both "HH:MM:SS" and "HH:MM" formats
    return time.slice(0, 5);
}

// Format course info like dropdown: "course_name - teacherName - duration - (courseType)"
export function formatCourseInfo(course) {
    if (!course) return '';
    
    const courseName = course.course_name || '';
    const teacherName = course.teacher 
        ? `${course.teacher.first_name || ''} ${course.teacher.last_name || ''}`.trim() 
        : 'بدون آموزگار';
    const duration = course.duration ? `${course.duration} دقیقه` : '';
    const courseTypeLabel = course.course_type === 'individual' ? 'خصوصی' : 'گروهی';
    
    const parts = [courseName, teacherName];
    if (duration) {
        parts.push(duration);
    }
    parts.push(`(${courseTypeLabel})`);
    return parts.join(' - ');
}

// Parse error messages from backend response
export function parseErrorMessage(error, errorResponse, mode = 'create') {
    // If no error response, return the error message or default
    if (!errorResponse || !errorResponse.data) {
        return error || `خطا در ${mode === 'edit' ? 'به‌روزرسانی' : 'ثبت‌نام'} هنرجو`;
    }

    const errorData = errorResponse.data;
    
    // If error is null, it means we should parse from response data
    // If error is provided and we only have generic detail, use the provided error
    if (error !== null) {
        const hasStructuredError = errorData.error_code || errorData.conflicts || 
            errorData.day || errorData.start_time || errorData.non_field_errors;
        
        // If we only have a generic detail and no structured error, use the provided error
        if (!hasStructuredError && errorData.detail) {
            // Use provided error if it's different from the generic detail
            if (error && error !== errorData.detail) {
                return error;
            }
            // Otherwise fall through to show the detail
        }
    }
    
    // Handle time conflict errors
    if (errorData.error_code === 'student_time_conflict' || errorData.error_code === 'teacher_time_conflict') {
        if (errorData.conflicts && errorData.conflicts.length > 0) {
            const conflict = errorData.conflicts[0];
            
            // Extract values (handling ErrorDetail objects)
            const startTime = formatTimeForError(conflict.start_time);
            const endTime = formatTimeForError(conflict.end_time);
            const courseName = extractValue(conflict.conflicting_course_name);
            
            // Build time range string
            let timeRange = '';
            if (startTime && endTime) {
                timeRange = ` (${startTime} - ${endTime})`;
            } else if (startTime) {
                timeRange = ` (${startTime})`;
            }
            
            // Build course name string
            let courseInfo = '';
            if (courseName) {
                courseInfo = ` دوره "${courseName}"`;
            }
            
            if (errorData.error_code === 'student_time_conflict') {
                return `تداخل زمانی با برنامه هنرجو: این هنرجو در این بازه${timeRange}${courseInfo} کلاس دیگری دارد.`;
            } else {
                return `تداخل زمانی با برنامه آموزگار: آموزگار در این بازه${timeRange}${courseInfo} کلاس دیگری دارد.`;
            }
        }
        // Fallback if no conflicts array
        const dayError = extractValue(errorData.day);
        if (dayError) {
            return dayError;
        }
        return 'تداخل زمانی با کلاس‌های دیگر وجود دارد.';
    }

    // Handle duplicate session enrollment
    if (errorData.error_code === 'duplicate_session_enrollment') {
        return 'این هنرجو قبلاً در همین زمان از این دوره ثبت‌نام کرده است.';
    }

    // Handle field-specific errors (check day field for conflict errors first)
    if (errorData.day) {
        const dayError = Array.isArray(errorData.day) 
            ? errorData.day.map(e => extractValue(e)).join('، ')
            : extractValue(errorData.day);
        
        // If day error contains conflict message, check if we have conflict details
        if (dayError && (dayError.includes('تداخل') || dayError.includes('conflict'))) {
            // Try to get more details from conflicts array if available
            if (errorData.conflicts && errorData.conflicts.length > 0) {
                const conflict = errorData.conflicts[0];
                const startTime = formatTimeForError(conflict.start_time);
                const endTime = formatTimeForError(conflict.end_time);
                const courseName = extractValue(conflict.conflicting_course_name);
                
                let timeRange = '';
                if (startTime && endTime) {
                    timeRange = ` (${startTime} - ${endTime})`;
                } else if (startTime) {
                    timeRange = ` (${startTime})`;
                }
                
                let courseInfo = '';
                if (courseName) {
                    courseInfo = ` دوره "${courseName}"`;
                }
                
                // Determine if it's student or teacher conflict based on error_code
                if (errorData.error_code === 'student_time_conflict') {
                    return `تداخل زمانی با برنامه هنرجو: این هنرجو در این بازه${timeRange}${courseInfo} کلاس دیگری دارد.`;
                } else if (errorData.error_code === 'teacher_time_conflict') {
                    return `تداخل زمانی با برنامه آموزگار: آموزگار در این بازه${timeRange}${courseInfo} کلاس دیگری دارد.`;
                } else {
                    return `تداخل زمانی:${timeRange}${courseInfo}`;
                }
            }
        }
        return dayError;
    }

    if (errorData.start_time) {
        const startTimeError = Array.isArray(errorData.start_time) 
            ? errorData.start_time.map(e => extractValue(e)).join('، ')
            : extractValue(errorData.start_time);
        return startTimeError;
    }

    if (errorData.discount_amount) {
        const discountError = Array.isArray(errorData.discount_amount) 
            ? errorData.discount_amount.map(e => extractValue(e)).join('، ')
            : extractValue(errorData.discount_amount);
        return discountError;
    }

    // Course/slot errors (time_slot_conflict, capacity_exceeded, max_sessions_reached…):
    // the backend message names who holds the slot — never collapse it into the
    // generic fallback, or staff retry blindly / re-create the student.
    if (errorData.teacher_course_id || errorData.course) {
        const v = errorData.teacher_course_id || errorData.course;
        return Array.isArray(v) ? v.map(e => extractValue(e)).join('، ') : extractValue(v);
    }

    // Handle non-field errors
    if (errorData.non_field_errors) {
        const nonFieldError = Array.isArray(errorData.non_field_errors) 
            ? errorData.non_field_errors.map(e => extractValue(e)).join('، ')
            : extractValue(errorData.non_field_errors);
        return nonFieldError;
    }

    // If we have a detail message, use it (fallback for generic errors)
    if (errorData.detail) {
        const detailError = Array.isArray(errorData.detail) 
            ? errorData.detail.map(e => extractValue(e)).join('، ')
            : extractValue(errorData.detail);
        return detailError;
    }
    
    // Fallback to error message or default
    return error || `خطا در ${mode === 'edit' ? 'به‌روزرسانی' : 'ثبت‌نام'} هنرجو`;
}

